import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Building2, Compass, Loader2, LogOut, Mail, RefreshCw, Sparkles, Users } from "lucide-react";
import { LOGO } from "../data";
import ExperiencesManager from "./admin/ExperiencesManager";
import PropertiesManager from "./admin/PropertiesManager";
import ConciergeSettings from "./admin/ConciergeSettings";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "ogstay-admin-token";

const inputCls =
  "w-full rounded-xl border border-navy/15 bg-warm px-4 py-3 text-sm font-semibold text-ink placeholder-mute/70 transition-[border-color,box-shadow] duration-300 focus:border-sky focus:outline-none focus:ring-2 focus:ring-sky/25";

const TABS = [
  { key: "valuations", label: "Valutazioni", endpoint: "valuations", testid: "admin-tab-valuations" },
  { key: "enquiries", label: "Richieste info", endpoint: "enquiries", testid: "admin-tab-enquiries" },
  { key: "leads", label: "Contatti & iscrizioni", endpoint: "leads", testid: "admin-tab-leads" },
];

const MANAGER_TABS = [
  { key: "properties", label: "Case", testid: "admin-tab-properties" },
  { key: "experiences", label: "Esperienze", testid: "admin-tab-experiences" },
  { key: "concierge", label: "Concierge AI", testid: "admin-tab-concierge" },
];

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("it-IT", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
};

function formatApiErrorDetail(detail) {
  if (detail == null) return "Qualcosa non ha funzionato. Riprova.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function AdminPage() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({ valuations: [], enquiries: [], leads: [] });
  const [active, setActive] = useState("valuations");
  const [refreshing, setRefreshing] = useState(false);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
  }, []);

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [v, e, l] = await Promise.all(TABS.map((t) => axios.get(`${API}/${t.endpoint}`, { headers })));
      setData({ valuations: v.data, enquiries: e.data, leads: l.data });
    } catch (err) {
      if (err.response?.status === 401) logout();
    } finally {
      setRefreshing(false);
    }
  }, [token, logout]);

  useEffect(() => {
    if (token) load();
  }, [token, load]);

  const login = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email: form.email.trim().toLowerCase(), password: form.password });
      localStorage.setItem(TOKEN_KEY, data.access_token);
      setToken(data.access_token);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div data-testid="admin-login" className="flex min-h-screen items-center justify-center bg-navy px-5 pt-20">
        <motion.form
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          data-testid="admin-login-form"
          onSubmit={login}
          className="w-full max-w-md rounded-[2rem] bg-warm p-9 shadow-2xl"
        >
          <div className="mb-8 flex justify-center">
            <img src={LOGO} alt="OGStay" className="h-11 w-auto" />
          </div>
          <h1 className="text-center font-display text-3xl font-semibold text-navy">Pannello OGStay</h1>
          <p className="mt-2 text-center text-sm text-mute">Area riservata al team.</p>
          <label className="mt-8 grid gap-2">
            <span className="text-xs font-extrabold uppercase tracking-[0.15em] text-navy">Email</span>
            <input data-testid="admin-email-input" type="email" required value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} placeholder="info@ogstay.com" className={inputCls} />
          </label>
          <label className="mt-5 grid gap-2">
            <span className="text-xs font-extrabold uppercase tracking-[0.15em] text-navy">Password</span>
            <input data-testid="admin-password-input" type="password" required value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} placeholder="••••••••" className={inputCls} />
          </label>
          {error && (
            <p data-testid="admin-login-error" className="mt-4 rounded-xl bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">
              {error}
            </p>
          )}
          <button data-testid="admin-login-submit" type="submit" disabled={loading} className="btn-primary mt-7 w-full disabled:opacity-60">
            {loading && <Loader2 className="h-4 w-4 animate-spin" />}
            {loading ? "Accesso in corso…" : "Accedi"}
          </button>
        </motion.form>
      </div>
    );
  }

  const isDataTab = TABS.some((t) => t.key === active);
  const rows = isDataTab ? data[active] : [];

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-warm pb-24 pt-28">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="kicker text-navy">
              <span className="h-px w-8 bg-sky" />
              Area riservata
            </p>
            <h1 className="mt-3 font-display text-3xl font-semibold tracking-tight text-navy sm:text-4xl">Pannello richieste</h1>
          </div>
          <div className="flex items-center gap-3">
            <button data-testid="admin-refresh-button" onClick={load} disabled={refreshing} className="btn-dark !px-5 !py-3 text-[13px]">
              <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
              Aggiorna
            </button>
            <button data-testid="admin-logout-button" onClick={logout} className="inline-flex items-center gap-2 rounded-full px-5 py-3 text-[13px] font-bold text-mute ring-1 ring-navy/15 transition-colors hover:bg-navy hover:text-white">
              <LogOut className="h-4 w-4" />
              Esci
            </button>
          </div>
        </div>

        <div className="mt-10 grid gap-5 sm:grid-cols-3">
          {TABS.map((t, i) => {
            const Icon = [Building2, Mail, Users][i];
            return (
              <button
                key={t.key}
                data-testid={`admin-stat-${t.key}`}
                onClick={() => setActive(t.key)}
                className={`flex items-center gap-5 rounded-[1.75rem] p-7 text-left ring-1 transition-[transform,box-shadow,background-color] duration-300 hover:-translate-y-1 ${
                  active === t.key ? "bg-navy text-white ring-navy" : "bg-white ring-navy/10 hover:shadow-lg"
                }`}
              >
                <span className={`flex h-12 w-12 items-center justify-center rounded-full ${active === t.key ? "bg-sky/15 text-sky" : "bg-sand text-navy"}`}>
                  <Icon className="h-5 w-5" />
                </span>
                <span>
                  <span className="block font-display text-3xl font-semibold">{data[t.key].length}</span>
                  <span className={`text-xs font-extrabold uppercase tracking-[0.15em] ${active === t.key ? "text-white/60" : "text-mute"}`}>{t.label}</span>
                </span>
              </button>
            );
          })}
        </div>

        <div className="mt-8 flex flex-wrap gap-2.5">
          {[...TABS, ...MANAGER_TABS].map((t) => {
            const isManager = MANAGER_TABS.some((m) => m.key === t.key);
            const Icon = isManager ? (t.key === "experiences" ? Compass : Sparkles) : null;
            return (
              <button
                key={t.key}
                data-testid={t.testid}
                onClick={() => setActive(t.key)}
                className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-xs font-extrabold uppercase tracking-[0.12em] transition-colors duration-300 ${
                  active === t.key ? "bg-navy text-white" : "bg-white text-mute ring-1 ring-navy/15 hover:text-navy"
                }`}
              >
                {Icon && <Icon className="h-3.5 w-3.5" />}
                {t.label}
              </button>
            );
          })}
        </div>

        <div className="mt-8">
          {active === "properties" && <PropertiesManager token={token} />}
          {active === "experiences" && <ExperiencesManager token={token} />}
          {active === "concierge" && <ConciergeSettings token={token} />}
          {isDataTab && (
            <div data-testid={`admin-table-${active}`} className="overflow-x-auto rounded-[1.75rem] bg-white ring-1 ring-navy/10">
              {rows.length === 0 ? (
                <p className="px-8 py-16 text-center text-sm font-semibold text-mute">Nessuna richiesta per ora.</p>
              ) : (
                <table className="w-full min-w-[760px] text-left text-sm">
                  <thead>
                    <tr className="border-b border-navy/10 text-[11px] font-extrabold uppercase tracking-[0.15em] text-mute">
                      <th className="px-6 py-4">Data</th>
                      <th className="px-6 py-4">Nome</th>
                      <th className="px-6 py-4">Email</th>
                      {active === "valuations" && (
                        <>
                          <th className="px-6 py-4">Località</th>
                          <th className="px-6 py-4">Tipologia</th>
                          <th className="px-6 py-4">Dettagli</th>
                        </>
                      )}
                      {active === "enquiries" && (
                        <>
                          <th className="px-6 py-4">Oggetto</th>
                          <th className="px-6 py-4">Messaggio</th>
                        </>
                      )}
                      {active === "leads" && <th className="px-6 py-4">Tipo</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, ri) => (
                      <tr key={r.id || r._id || ri} className="border-b border-navy/5 align-top last:border-0 hover:bg-sand/50">
                        <td className="whitespace-nowrap px-6 py-4 font-semibold text-mute">{fmtDate(r.created_at)}</td>
                        <td className="px-6 py-4 font-bold text-navy">{r.name}</td>
                        <td className="px-6 py-4 font-semibold text-ink">{r.email}</td>
                        {active === "valuations" && (
                          <>
                            <td className="px-6 py-4 font-semibold">{r.location}</td>
                            <td className="px-6 py-4 font-semibold">{r.property_type}</td>
                            <td className="max-w-[16rem] px-6 py-4 text-xs leading-relaxed text-mute">
                              {[r.objective, r.contact_pref && `Ricontatto: ${r.contact_pref}`, r.role, r.units && `Unità: ${r.units}`, r.bedrooms && `Camere: ${r.bedrooms}`, r.beds && `Letti: ${r.beds}`, r.guests && `Ospiti: ${r.guests}`, r.status, (r.platforms || []).join(", "), r.airbnb_link, r.message].filter(Boolean).join(" · ") || "—"}
                            </td>
                          </>
                        )}
                        {active === "enquiries" && (
                          <>
                            <td className="px-6 py-4 font-semibold">{r.subject}</td>
                            <td className="max-w-[20rem] px-6 py-4 text-xs leading-relaxed text-mute">{r.message}</td>
                          </>
                        )}
                        {active === "leads" && <td className="px-6 py-4 font-semibold capitalize">{r.kind}</td>}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
